import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmptyState, FormError, Grid, HeaderActions, Inline, Section, Skeleton, StatusChip, Surface } from '../components/ui';
import type { ActionDescriptor } from '../components/ui';
import { WorkflowLayout } from '../components/ui/PageLayouts';
import { SubjectForm } from '../components/sites/SubjectForm';
import { domainIcon } from '../components/sites/domainIcon';
import { getSubject, updateSubject, subjectsKeys, type Subject } from '../lib/api/subjects.gen';
import { queryOrNull } from '../lib/query/queryFn';
import { errorMessage } from '../lib/errorMessage';
import { formatDistanceToNow } from '../lib/time';
import { useVocab, SITES_REGISTRY_LABEL } from '../lib/vocab';
import { useCapabilities } from '../lib/capabilities';

// /app/sites/:id — one record (P03 §7.1; story 3-1b).
//
// SCOPE SAFETY IS THE POINT OF THIS PAGE'S NOT-FOUND STATE. The endpoint
// answers 404 for a record in another tenant AND for one outside the
// principal's scope inside their own tenant — the two are deliberately
// indistinguishable, so this page must render ONE state for both. Anything
// that said "you don't have access to this record" would confirm the id is
// real, which is exactly the leak P03 §7.1 forbids.
//
// ── 2-147wc: registry migration onto `WorkflowLayout` ─────────────────────
// P10 §2.6/§2.7 names this page as the THIRD real Workflow-family header
// drift (alongside `ReviewDetailPage.tsx`) — a hand-rolled `<header><h1>`
// never wired to `PageHeader`, migration assigned to this story explicitly
// ("Page header kind #2 is the resolved canonical target for all three...
// the actual migration is 2-147wc's job"). Shape (c) of the family's three
// panel shapes — no side panel — so `WorkflowLayout` renders with no
// `panel` prop, a single stacked column, matching the original's plain
// `space-y-6`.
//
// The former hand-rolled "Back to Sites" link (with its own `ArrowLeftIcon`)
// is REMOVED, not relocated: `/app/sites/:id` already produces a real
// "Dashboard › Sites" crumb via `TopBar`'s `resolveBreadcrumbs` (the `Sites`
// nav entry's `to: '/app/sites'` is a prefix of this route) — P10's own
// breadcrumb placement rule states this trail is shell-owned, so a page
// restating its own way back is exactly the duplication that rule forbids.
// Verified live: the crumb renders at both 1440px and 390px (`TopBar` never
// hides it below a breakpoint), so nothing is lost — confirmed via a fresh
// grep of every e2e spec touching this page (`story-3-1b.spec.mjs`), which
// asserts headings only, never the removed link's text or role.
//
// The decorative icon avatar square (`h-10 w-10 rounded-lg bg-muted`) is
// also dropped rather than relocated — `PageHeaderProps.title` is a plain
// `string` (no ReactNode slot for a leading icon), and no other converged
// Workflow-family header (`ReportDetailPage.tsx`) carries one either. The
// domain icon still renders, just smaller and inline beside the descriptive
// text in the header's `description` slot (`Inline`, story 2-147sa) rather
// than as a standalone avatar — the domain is still identified visually and
// in the `noun` text right beside it, not silently lost.

const attributeLabel = (pack: { subjectSchema: { fields: Array<{ key: string; label: string }> } } | undefined, key: string): string =>
  pack?.subjectSchema.fields.find((field) => field.key === key)?.label ?? key;

function renderAttribute(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'object') {
    const parts = Object.entries(value as Record<string, unknown>)
      .filter(([k, v]) => v !== '' && v !== null && v !== undefined && k !== 'lat' && k !== 'lng')
      .map(([, v]) => String(v));
    return parts.length ? parts.join(', ') : '—';
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export function SiteDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { vocab, packs } = useVocab();
  const { hasPermission } = useCapabilities();
  const [editing, setEditing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    data: subject,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: subjectsKeys.detail(id),
    // G-14/G-20: the shared helper owns 404 → null, so "not found" is DATA
    // here, not an error state to distinguish from a network failure.
    queryFn: () => queryOrNull(getSubject(id).then((response) => response.data)),
    enabled: Boolean(id),
  });

  const archiveMutation = useMutation({
    mutationFn: async (archived: boolean) => (await updateSubject(id, { archived })).data,
    onSuccess: async (updated: Subject) => {
      queryClient.setQueryData(subjectsKeys.detail(id), updated);
      await queryClient.invalidateQueries({ queryKey: subjectsKeys.lists() });
      setActionError(null);
    },
    onError: () => setActionError('That change could not be saved. Nothing was modified — try again.'),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        title="That record could not be loaded"
        // G-27: the server's own sentence, not a generic guess.
        description={errorMessage(error)}
        actionLabel="Retry"
        onAction={() => void refetch()}
      />
    );
  }

  if (!subject) {
    // One state for "does not exist" and "not yours" — see the note above.
    return (
      <EmptyState
        title="Record not found"
        description={`This record doesn't exist, or it isn't in your ${SITES_REGISTRY_LABEL.toLowerCase()}.`}
        actionLabel={`Back to ${SITES_REGISTRY_LABEL}`}
        onAction={() => navigate('/app/sites')}
      />
    );
  }

  const pack = packs.find((p) => p.key === subject.domainKey);
  const Icon = domainIcon(pack?.icon);
  const noun = vocab('Subject', subject.domainKey);
  const canManage = hasPermission('subjects:manage');
  const attributeKeys = Object.keys(subject.attributes ?? {});

  // Story 2-146b: this site's own lifecycle (`/app/sites/:id` is a
  // `workflow`-family route as of this story) — raw ternary `Badge`
  // converted to `StatusChip`, same tone convention `TemplateCard`'s
  // `STATUS_META` set for a retired-but-fine `archived` state.
  const statusChip = subject.archivedAt ? (
    <StatusChip tone="neutral" weight="bold" label="Archived" value="archived" />
  ) : (
    <StatusChip tone="success" label="Active" value="active" />
  );

  const headerActions: ActionDescriptor[] = [
    ...(canManage && !editing
      ? [{ id: 'edit', label: 'Edit', variant: 'secondary' as const, onClick: () => setEditing(true) }]
      : []),
    ...(canManage
      ? [
          {
            id: 'archive',
            label: subject.archivedAt ? 'Restore' : 'Archive',
            variant: 'secondary' as const,
            loading: archiveMutation.isPending,
            onClick: () => archiveMutation.mutate(!subject.archivedAt),
          },
        ]
      : []),
  ];

  return (
    <WorkflowLayout
      header={{
        title: subject.name,
        description: (
          <Inline gap={1.5}>
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span>
              {noun}
              {subject.referenceCode ? ` · ${subject.referenceCode}` : ''}
            </span>
          </Inline>
        ),
        chips: statusChip,
        actions: <HeaderActions actions={headerActions} />,
      }}
    >
      <FormError message={actionError} />

      {editing && pack ? (
        <Surface as="section" variant="flat" padding="xl">
          <Section as="div" title="Edit details">
            <SubjectForm
              key={pack.key}
              pack={pack}
              subject={subject}
              onCancel={() => setEditing(false)}
              onSaved={() => setEditing(false)}
            />
          </Section>
        </Surface>
      ) : (
        <Surface as="section" variant="flat" padding="xl">
          <Section as="div" title="Details" gap={4}>
            <Grid as="dl" columns={{ base: 1, sm: 2 }} gap={4}>
              {attributeKeys.length === 0 && (
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Details</dt>
                  <dd className="text-sm text-foreground">No additional details recorded.</dd>
                </div>
              )}
              {attributeKeys.map((key) => (
                <div key={key}>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">{attributeLabel(pack, key)}</dt>
                  <dd className="break-words text-sm text-foreground">
                    {renderAttribute((subject.attributes as Record<string, unknown>)[key])}
                  </dd>
                </div>
              ))}
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Last inspection</dt>
                <dd className="text-sm text-foreground">
                  {subject.lastInspectionAt ? formatDistanceToNow(subject.lastInspectionAt) : 'Never'}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Next scheduled</dt>
                <dd className="text-sm text-foreground">
                  {subject.nextScheduledAt ? new Date(subject.nextScheduledAt).toLocaleString() : 'Not scheduled'}
                </dd>
              </div>
            </Grid>
            <Link
              to={`/app/items?propertyId=${subject._id}`}
              className="focus-ring inline-flex text-sm font-semibold text-primary"
            >
              View inspections for this record
            </Link>
          </Section>
        </Surface>
      )}
    </WorkflowLayout>
  );
}
